import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { environment } from '../environments/environment';
import { EARLY_CHILDHOOD_REGISTRY } from './core/curriculum/early-childhood-catalog';
import { CURRICULUM_SKILLS, CURRICULUM_SOURCES, CurriculumSource } from './core/curriculum/curriculum';
import type {
  EarlyChildhoodActivityProvenance,
  EarlyChildhoodAgeBand,
  EarlyChildhoodInstitutionalSource
} from './core/curriculum/early-childhood-provenance';
import {
  buildDiagnosticExport,
  DiagnosticExportIntegrityError
} from './core/diagnostics/diagnostic-export';
import {
  ActivityMenuGroup,
  buildActivityMenuGroups,
  exerciseVolumeLabel
} from './core/exercises/activity-menu';
import {
  EXERCISES,
  ExerciseDefinition,
  ExerciseKind,
  ExerciseQuestion,
  generateExerciseSession
} from './core/exercises/exercise-generator';
import { ChildProfile, SkillProgress } from './core/models/learning.models';
import {
  EARLY_CHILDHOOD_AGE_OPTIONS,
  SCHOOL_AGE_OPTIONS,
  earlyChildhoodOption,
  isEarlyChildhoodProfile,
  profileAgeLabel as formatProfileAge,
  profileScreenPolicy
} from './core/models/profile-stage';
import {
  SpeechLocale,
  SpeechMode,
  SpeechService,
  SpeechVoiceDiagnostic
} from './core/speech/speech.service';
import {
  CaregiverActivityStatus,
  CaregiverActivityStore
} from './core/storage/caregiver-activity-store';
import { LocalLearningStore } from './core/storage/local-learning-store';
import { AudioSettingsScreenComponent } from './ui/audio-settings-screen.component';
import { CompleteScreenComponent } from './ui/complete-screen.component';
import {
  ExerciseScreenComponent,
  ExerciseScreenViewModel
} from './ui/exercise-screen.component';

type Screen =
  | 'loading'
  | 'choose-profile'
  | 'create-profile'
  | 'edit-profile'
  | 'caregiver-home'
  | 'activity-menu'
  | 'exercise'
  | 'complete'
  | 'curriculum'
  | 'audio-settings'
  | 'error';
type Feedback = 'correct' | 'incorrect' | null;

interface OrderTile {
  position: number;
  visual: string;
  label: string;
}

@Component({
  selector: 'app-root',
  imports: [AudioSettingsScreenComponent, CompleteScreenComponent, ExerciseScreenComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  private readonly store = inject(LocalLearningStore);
  private readonly caregiverActivityStore = inject(CaregiverActivityStore);
  private readonly speech = inject(SpeechService);
  private readonly curriculumReturnScreen = signal<Screen>('choose-profile');
  private readonly audioSettingsReturnScreen = signal<Screen>('choose-profile');
  private readonly caregiverStatusVersion = signal(0);

  protected readonly environment = environment;
  protected readonly screen = signal<Screen>('loading');
  protected readonly profiles = signal<ChildProfile[]>([]);
  protected readonly activeProfile = signal<ChildProfile | null>(null);
  protected readonly nickname = signal('');
  protected readonly age = signal(6);
  protected readonly earlyAgeBand = signal<EarlyChildhoodAgeBand | null>(null);
  protected readonly editingProfile = signal<ChildProfile | null>(null);
  protected readonly editNickname = signal('');
  protected readonly editAge = signal(6);
  protected readonly editEarlyAgeBand = signal<EarlyChildhoodAgeBand | null>(null);
  protected readonly deleteConfirmation = signal(false);
  protected readonly questionIndex = signal(0);
  protected readonly feedback = signal<Feedback>(null);
  protected readonly saving = signal(false);
  protected readonly exporting = signal(false);
  protected readonly exportStatus = signal<string | null>(null);
  protected readonly speechDiagnostics = this.speech.diagnostics;
  protected readonly speechMode = this.speech.mode;
  protected readonly speechBusyLocale = signal<SpeechLocale | null>(null);
  protected readonly speechSettingsStatus = signal<string | null>(null);
  protected readonly lifetimeProgress = signal<SkillProgress>({ attempts: 0, correct: 0 });
  protected readonly sessionAttempts = signal(0);
  protected readonly orderSelection = signal<number[]>([]);
  protected readonly ages = SCHOOL_AGE_OPTIONS;
  protected readonly earlyChildhoodAgeOptions = EARLY_CHILDHOOD_AGE_OPTIONS;
  protected readonly curriculumSources = CURRICULUM_SOURCES;
  protected readonly curriculumSkills = CURRICULUM_SKILLS;
  protected exercises: readonly ExerciseDefinition[] = EXERCISES;
  protected activityGroups: readonly ActivityMenuGroup[] = buildActivityMenuGroups(EXERCISES);
  protected readonly exerciseVolumeLabel = exerciseVolumeLabel;
  protected readonly activeExercise = signal<ExerciseDefinition>(EXERCISES[0]);
  protected readonly questions = signal<ExerciseQuestion[]>(generateExerciseSession('quantity'));

  protected readonly currentQuestion = computed(
    (): ExerciseQuestion => this.questions()[this.questionIndex()] ?? this.questions()[0]!
  );
  protected readonly questionCount = computed(() => this.questions().length);
  protected readonly progressPercent = computed(() =>
    this.questionCount() ? ((this.questionIndex() + 1) / this.questionCount()) * 100 : 0
  );
  protected readonly exerciseScreenView = computed<ExerciseScreenViewModel | null>(() => {
    const profile = this.activeProfile();
    if (!profile) {
      return null;
    }

    const question = this.currentQuestion();
    let questionView: ExerciseScreenViewModel['question'];
    let tapOrder: ExerciseScreenViewModel['tapOrder'] = null;

    switch (question.kind) {
      case 'quantity':
        questionView = {
          kind: 'quantity',
          instruction: this.displayInstruction(question),
          symbol: question.symbol,
          count: question.count,
          choices: question.choices
        };
        break;
      case 'compare':
        questionView = {
          kind: 'compare',
          instruction: this.displayInstruction(question),
          groups: question.groups.map((group) => ({
            ...group,
            label: `${group.count} oggetti`
          }))
        };
        break;
      case 'sequence':
        questionView = {
          kind: 'sequence',
          instruction: this.displayInstruction(question),
          pattern: question.pattern,
          layout: question.familyId === 'visual-addition-within-5' || question.familyId === 'visual-subtraction-within-5'
            ? 'arithmetic'
            : 'sequence',
          choices: question.choices
        };
        break;
      case 'instruction':
        if (this.isTapOrderQuestion(question)) {
          questionView = {
            kind: 'tap-order',
            instruction: this.displayInstruction(question)
          };
          tapOrder = {
            tiles: this.orderTiles(question).map((tile) => ({
              ...tile,
              selected: this.orderTileSelected(tile.position)
            })),
            selectedVisuals: [
              this.orderSelectedVisual(question, 0),
              this.orderSelectedVisual(question, 1)
            ],
            canReset: this.orderSelection().length === 1
          };
        } else {
          questionView = {
            kind: 'instruction-choice',
            instruction: this.displayInstruction(question),
            choices: question.choices
          };
        }
        break;
    }

    return {
      profileNickname: profile.nickname,
      domainLabel: this.activeExercise().domainLabel,
      question: questionView,
      questionNumber: this.questionIndex() + 1,
      questionCount: this.questionCount(),
      progressPercent: this.progressPercent(),
      feedback: this.feedback(),
      busy: this.saving(),
      canAdvance: this.feedback() === 'correct',
      tapOrder
    };
  });
  protected readonly canCreateProfile = computed(() => this.nickname().trim().length > 0 && !this.saving());
  protected readonly canSaveProfile = computed(
    () => this.editingProfile() !== null && this.editNickname().trim().length > 0 && !this.saving()
  );

  async ngOnInit(): Promise<void> {
    try {
      const profiles = await this.store.listProfiles();
      this.profiles.set(profiles);
      const activeId = await this.store.getActiveProfileId();
      const active = profiles.find((profile) => profile.id === activeId) ?? null;

      if (active) {
        await this.selectProfile(active);
      } else {
        this.screen.set(profiles.length ? 'choose-profile' : 'create-profile');
      }
    } catch {
      this.screen.set('error');
    }
  }

  protected sourceFor(sourceId: string): CurriculumSource | undefined {
    return this.curriculumSources.find((source) => source.id === sourceId);
  }

  protected earlyChildhoodSourceFor(sourceId: string): EarlyChildhoodInstitutionalSource | undefined {
    return EARLY_CHILDHOOD_REGISTRY.sources.find((source) => source.id === sourceId);
  }

  protected caregiverActivitiesFor(profile: ChildProfile): readonly EarlyChildhoodActivityProvenance[] {
    if (!profile.earlyChildhoodAgeBand) {
      return [];
    }
    return EARLY_CHILDHOOD_REGISTRY.activities.filter(
      (activity) =>
        activity.ageBands.includes(profile.earlyChildhoodAgeBand!) &&
        activity.mode === 'caregiver-mediated' &&
        activity.status === 'implemented'
    );
  }

  protected caregiverActivityStatus(activityId: string): CaregiverActivityStatus | null {
    this.caregiverStatusVersion();
    const profile = this.activeProfile();
    return profile ? this.caregiverActivityStore.get(profile.id, activityId) : null;
  }

  protected setCaregiverActivityStatus(activityId: string, status: CaregiverActivityStatus): void {
    const profile = this.activeProfile();
    if (!profile || !isEarlyChildhoodProfile(profile)) {
      return;
    }
    this.caregiverActivityStore.set(profile.id, activityId, status);
    this.caregiverStatusVersion.update((value) => value + 1);
  }

  protected range(count: number): readonly number[] {
    return Array.from({ length: count }, (_, index) => index);
  }

  protected profileAgeLabel(profile: ChildProfile): string {
    return formatProfileAge(profile);
  }

  protected profilePolicy(profile: ChildProfile) {
    return profileScreenPolicy(profile);
  }

  protected openCurriculum(): void {
    const current = this.screen();
    if (current !== 'curriculum' && current !== 'loading' && current !== 'error') {
      this.curriculumReturnScreen.set(current);
    }
    this.exportStatus.set(null);
    this.screen.set('curriculum');
  }

  protected closeCurriculum(): void {
    const target = this.curriculumReturnScreen();
    this.screen.set(target === 'curriculum' ? (this.profiles().length ? 'choose-profile' : 'create-profile') : target);
  }

  protected async openAudioSettings(): Promise<void> {
    const current = this.screen();
    if (current !== 'audio-settings' && current !== 'loading' && current !== 'error') {
      this.audioSettingsReturnScreen.set(current);
    }
    this.speechSettingsStatus.set(null);
    this.screen.set('audio-settings');
    await this.refreshSpeechDiagnostics();
  }

  protected closeAudioSettings(): void {
    const target = this.audioSettingsReturnScreen();
    this.screen.set(
      target === 'audio-settings'
        ? (this.profiles().length ? 'choose-profile' : 'create-profile')
        : target
    );
  }

  protected setSpeechMode(mode: SpeechMode): void {
    this.speech.setMode(mode);
    this.speechSettingsStatus.set(
      mode === 'device'
        ? 'Voce del dispositivo attiva. Il motore locale è disattivato.'
        : 'Voce locale Learning Garden attiva.'
    );
  }

  protected async refreshSpeechDiagnostics(): Promise<void> {
    try {
      await this.speech.refreshDiagnostics();
    } catch {
      this.speechSettingsStatus.set('Non riesco a rileggere lo stato delle voci su questo dispositivo.');
    }
  }

  protected async prepareSpeechVoice(locale: SpeechLocale): Promise<void> {
    if (this.speechBusyLocale()) {
      return;
    }
    this.speechBusyLocale.set(locale);
    this.speechSettingsStatus.set(
      locale === 'en-US' ? 'Preparo Lessac e verifico i file offline…' : 'Preparo Paola e verifico i file offline…'
    );
    try {
      await this.speech.prepareVoice(locale);
      this.speechSettingsStatus.set(
        locale === 'en-US' ? 'Lessac è pronta e verificata in locale.' : 'Paola è pronta e verificata in locale.'
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.speechSettingsStatus.set(`Preparazione non riuscita: ${message}`);
    } finally {
      this.speechBusyLocale.set(null);
      await this.refreshSpeechDiagnostics();
    }
  }

  protected async forceDownloadSpeechVoice(locale: SpeechLocale): Promise<void> {
    if (this.speechBusyLocale()) {
      return;
    }
    this.speechBusyLocale.set(locale);
    this.speechSettingsStatus.set(
      locale === 'en-US' ? 'Elimino la cache di Lessac e la riscarico…' : 'Elimino la cache di Paola e la riscarico…'
    );
    try {
      await this.speech.forceDownloadVoice(locale);
      this.speechSettingsStatus.set(
        locale === 'en-US' ? 'Lessac è stata riscaricata e verificata.' : 'Paola è stata riscaricata e verificata.'
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.speechSettingsStatus.set(`Riscaricamento non riuscito: ${message}`);
    } finally {
      this.speechBusyLocale.set(null);
      await this.refreshSpeechDiagnostics();
    }
  }

  protected async testSpeechVoice(locale: SpeechLocale): Promise<void> {
    if (this.speechBusyLocale()) {
      return;
    }
    this.speechBusyLocale.set(locale);
    this.speechSettingsStatus.set(
      locale === 'en-US' ? 'Preparo il test di Lessac…' : 'Preparo il test di Paola…'
    );
    try {
      await this.speech.testVoice(locale);
      this.speechSettingsStatus.set(
        this.speechMode() === 'device'
          ? 'Test avviato con la voce del dispositivo.'
          : 'Test avviato con il motore locale.'
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.speechSettingsStatus.set(`Test non riuscito: ${message}`);
    } finally {
      this.speechBusyLocale.set(null);
      await this.refreshSpeechDiagnostics();
    }
  }

  protected speechVoiceStateLabel(voice: SpeechVoiceDiagnostic): string {
    switch (voice.state) {
      case 'ready':
        return 'Pronta';
      case 'loading':
        return 'Caricamento';
      case 'cached':
        return 'In cache';
      case 'failed':
        return 'Errore';
      default:
        return 'Da preparare';
    }
  }

  protected speechCacheLabel(voice: SpeechVoiceDiagnostic): string {
    switch (voice.cacheState) {
      case 'validated':
        return 'Verificata e disponibile offline';
      case 'present':
        return 'File presenti; verifica al caricamento';
      case 'missing':
        return 'Download offline incompleto o assente';
      default:
        return 'Stato cache non ancora noto';
    }
  }

  protected speechRouteLabel(voice: SpeechVoiceDiagnostic): string {
    switch (voice.lastRoute) {
      case 'local':
        return 'Motore locale';
      case 'local-loading':
        return 'In attesa del motore locale';
      case 'browser-fallback':
        return 'Fallback del dispositivo';
      case 'unavailable':
        return 'Audio non disponibile';
      default:
        return 'Non ancora usata in questa sessione';
    }
  }

  protected speechPackSizeLabel(voice: SpeechVoiceDiagnostic): string {
    if (!voice.packSizeBytes) {
      return 'non disponibile';
    }
    const megabytes = voice.packSizeBytes / (1024 * 1024);
    return `${megabytes.toFixed(megabytes >= 10 ? 0 : 1)} MB`;
  }

  protected async exportDiagnosticData(): Promise<void> {
    if (this.exporting()) {
      return;
    }

    this.exporting.set(true);
    this.exportStatus.set(null);
    try {
      const attempts = await this.store.listAttempts();
      const report = buildDiagnosticExport(attempts);
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = report.generatedAt.replace(/[:.]/g, '-');
      link.href = url;
      link.download = `learning-garden-diagnostic-${timestamp}.json`;
      link.click();
      URL.revokeObjectURL(url);
      this.exportStatus.set(
        attempts.length
          ? `Esportati ${attempts.length} tentativi senza nomi, età, ID profilo o timestamp dei singoli tentativi.`
          : 'Nessun tentativo ancora registrato: esportato un report vuoto.'
      );
    } catch (error) {
      this.exportStatus.set(
        error instanceof DiagnosticExportIntegrityError
          ? 'I dati diagnostici non sono coerenti: esportazione annullata senza esporre identificatori.'
          : 'Impossibile creare il file diagnostico su questo dispositivo.'
      );
    } finally {
      this.exporting.set(false);
    }
  }

  protected updateNickname(event: Event): void {
    this.nickname.set((event.target as HTMLInputElement).value);
  }

  protected selectAge(age: number): void {
    this.age.set(age);
    this.earlyAgeBand.set(null);
  }

  protected selectEarlyAgeBand(band: EarlyChildhoodAgeBand): void {
    const option = earlyChildhoodOption(band);
    if (!option) {
      return;
    }
    this.earlyAgeBand.set(band);
    this.age.set(option.approximateAge);
  }

  protected showCreateProfile(): void {
    this.nickname.set('');
    this.age.set(6);
    this.earlyAgeBand.set(null);
    this.screen.set('create-profile');
  }

  protected cancelCreateProfile(): void {
    this.screen.set(this.profiles().length ? 'choose-profile' : 'create-profile');
  }

  protected async createProfile(): Promise<void> {
    if (!this.canCreateProfile()) {
      return;
    }

    this.saving.set(true);
    try {
      const profile = await this.store.createProfile(
        this.nickname(),
        this.age(),
        this.earlyAgeBand() ?? undefined
      );
      this.profiles.update((profiles) => [...profiles, profile]);
      await this.selectProfile(profile);
    } catch {
      this.screen.set('error');
    } finally {
      this.saving.set(false);
    }
  }

  protected showEditProfile(profile: ChildProfile): void {
    this.editingProfile.set(profile);
    this.editNickname.set(profile.nickname);
    this.editAge.set(profile.age);
    this.editEarlyAgeBand.set(profile.earlyChildhoodAgeBand ?? null);
    this.deleteConfirmation.set(false);
    this.screen.set('edit-profile');
  }

  protected updateEditNickname(event: Event): void {
    this.editNickname.set((event.target as HTMLInputElement).value);
  }

  protected selectEditAge(age: number): void {
    this.editAge.set(age);
    this.editEarlyAgeBand.set(null);
  }

  protected selectEditEarlyAgeBand(band: EarlyChildhoodAgeBand): void {
    const option = earlyChildhoodOption(band);
    if (!option) {
      return;
    }
    this.editEarlyAgeBand.set(band);
    this.editAge.set(option.approximateAge);
  }

  protected cancelEditProfile(): void {
    this.editingProfile.set(null);
    this.deleteConfirmation.set(false);
    this.screen.set(this.profiles().length ? 'choose-profile' : 'create-profile');
  }

  protected async saveProfile(): Promise<void> {
    const profile = this.editingProfile();
    if (!profile || !this.canSaveProfile()) {
      return;
    }

    this.saving.set(true);
    try {
      const updated = await this.store.updateProfile(
        profile.id,
        this.editNickname(),
        this.editAge(),
        this.editEarlyAgeBand() ?? undefined
      );
      this.profiles.update((profiles) => profiles.map((item) => (item.id === updated.id ? updated : item)));
      if (this.activeProfile()?.id === updated.id) {
        this.activeProfile.set(updated);
      }
      this.editingProfile.set(null);
      this.deleteConfirmation.set(false);
      this.screen.set('choose-profile');
    } catch {
      this.screen.set('error');
    } finally {
      this.saving.set(false);
    }
  }

  protected requestDeleteProfile(): void {
    this.deleteConfirmation.set(true);
  }

  protected cancelDeleteProfile(): void {
    this.deleteConfirmation.set(false);
  }

  protected async deleteProfile(): Promise<void> {
    const profile = this.editingProfile();
    if (!profile || !this.deleteConfirmation() || this.saving()) {
      return;
    }

    this.saving.set(true);
    try {
      await this.store.deleteProfile(profile.id);
      this.caregiverActivityStore.removeForProfile(profile.id);
      const remaining = this.profiles().filter((item) => item.id !== profile.id);
      this.profiles.set(remaining);
      if (this.activeProfile()?.id === profile.id) {
        this.activeProfile.set(null);
      }
      this.editingProfile.set(null);
      this.deleteConfirmation.set(false);
      this.screen.set(remaining.length ? 'choose-profile' : 'create-profile');
    } catch {
      this.screen.set('error');
    } finally {
      this.saving.set(false);
    }
  }

  protected async selectProfile(profile: ChildProfile): Promise<void> {
    this.activeProfile.set(profile);
    await this.store.setActiveProfileId(profile.id);

    if (isEarlyChildhoodProfile(profile)) {
      this.setExercises([]);
      this.screen.set('caregiver-home');
      return;
    }

    this.setExercises(
      EXERCISES.filter(
        (exercise) => profile.age >= exercise.minAge && profile.age <= exercise.maxAge
      )
    );
    this.screen.set('activity-menu');
    this.speech.speak('Scegli un gioco.');
  }

  protected async chooseAnotherProfile(): Promise<void> {
    await this.store.setActiveProfileId(null);
    this.activeProfile.set(null);
    this.setExercises(EXERCISES);
    this.screen.set('choose-profile');
  }

  protected async startExercise(kind: ExerciseKind): Promise<void> {
    const profile = this.activeProfile();
    const definition = this.exercises.find((exercise) => exercise.kind === kind);
    if (
      !profile ||
      !definition ||
      isEarlyChildhoodProfile(profile) ||
      profile.age < definition.minAge ||
      profile.age > definition.maxAge
    ) {
      return;
    }

    this.activeExercise.set(definition);
    this.questions.set(generateExerciseSession(kind));
    this.resetSession();
    this.lifetimeProgress.set(await this.store.getProgress(profile.id, definition.skillId));
    this.screen.set('exercise');
    this.speakInstruction();
  }

  protected chooseAnotherActivity(): void {
    this.resetSession();
    const profile = this.activeProfile();
    if (profile && isEarlyChildhoodProfile(profile)) {
      this.screen.set('caregiver-home');
      return;
    }
    this.screen.set('activity-menu');
    this.speech.speak('Scegli un gioco.');
  }

  protected displayInstruction(question: ExerciseQuestion): string {
    if (this.isTapOrderQuestion(question)) {
      return question.instruction.replace(
        /\s*Quale sequenza è giusta\?$/u,
        ' Tocca le due immagini nell’ordine giusto.'
      );
    }
    return question.instruction;
  }

  protected speakInstruction(): void {
    this.speech.speak(this.displayInstruction(this.currentQuestion()), this.activeSpeechLocale());
  }

  protected isTapOrderQuestion(question: ExerciseQuestion): boolean {
    return this.activeExercise().kind === 'italian-listening-order-visible-bank' && question.kind === 'instruction';
  }

  protected orderTiles(question: ExerciseQuestion): readonly OrderTile[] {
    if (!this.isTapOrderQuestion(question) || question.kind !== 'instruction') {
      return [];
    }
    const correctChoice = question.choices.find((choice) => choice.value === question.expectedAnswer);
    if (!correctChoice) {
      return [];
    }
    const visuals = this.splitOrderVisual(correctChoice.visual);
    if (visuals.length !== 2) {
      return [];
    }
    const labels = this.splitOrderLabel(correctChoice.label);
    const tiles: OrderTile[] = visuals.map((visual, position) => ({
      position,
      visual,
      label: labels[position] ?? `passaggio ${position + 1}`
    }));
    return this.shouldReverseOrderTiles(question.id) ? [tiles[1]!, tiles[0]!] : tiles;
  }

  protected orderTileSelected(position: number): boolean {
    return this.orderSelection().includes(position);
  }

  protected orderSelectedVisual(question: ExerciseQuestion, slot: number): string | null {
    const position = this.orderSelection()[slot];
    if (position === undefined || question.kind !== 'instruction') {
      return null;
    }
    const correctChoice = question.choices.find((choice) => choice.value === question.expectedAnswer);
    if (!correctChoice) {
      return null;
    }
    return this.splitOrderVisual(correctChoice.visual)[position] ?? null;
  }

  protected clearOrderSelection(): void {
    if (!this.saving() && this.feedback() !== 'correct') {
      this.orderSelection.set([]);
      this.feedback.set(null);
    }
  }

  protected async selectOrderTile(position: number, question: ExerciseQuestion): Promise<void> {
    if (
      !this.isTapOrderQuestion(question) ||
      question.kind !== 'instruction' ||
      this.saving() ||
      this.feedback() === 'correct' ||
      this.orderTileSelected(position)
    ) {
      return;
    }

    if (this.feedback() === 'incorrect') {
      this.feedback.set(null);
    }
    const next = [...this.orderSelection(), position];
    this.orderSelection.set(next);
    if (next.length < 2) {
      return;
    }

    const answerValue = next[0] === 0 && next[1] === 1
      ? question.expectedAnswer
      : this.reversedOrderAnswerValue(question);
    await this.answer(answerValue);
    if (this.feedback() !== 'correct') {
      this.orderSelection.set([]);
    }
  }

  protected async answer(choice: number): Promise<void> {
    const profile = this.activeProfile();
    if (!profile || this.saving() || this.feedback() === 'correct') {
      return;
    }

    const question = this.currentQuestion();
    const correct = choice === question.expectedAnswer;
    this.saving.set(true);
    try {
      await this.store.recordAttempt({
        profileId: profile.id,
        skillId: question.skillId,
        questionId: question.id,
        selectedAnswer: choice,
        expectedAnswer: question.expectedAnswer,
        correct
      });
      this.sessionAttempts.update((value) => value + 1);
      this.lifetimeProgress.update((progress) => ({
        attempts: progress.attempts + 1,
        correct: progress.correct + (correct ? 1 : 0)
      }));
      this.feedback.set(correct ? 'correct' : 'incorrect');
      const locale = this.activeSpeechLocale();
      const feedbackText = correct
        ? locale === 'en-US' ? 'Correct!' : 'Giusto!'
        : locale === 'en-US' ? 'Listen again and try once more.' : question.retryInstruction;
      this.speech.speak(feedbackText, locale);
    } catch {
      this.screen.set('error');
    } finally {
      this.saving.set(false);
    }
  }

  protected nextQuestion(): void {
    if (this.feedback() !== 'correct') {
      return;
    }

    if (this.questionIndex() === this.questionCount() - 1) {
      this.screen.set('complete');
      this.speech.speak('Hai finito! Ottimo lavoro!');
      return;
    }

    this.questionIndex.update((index) => index + 1);
    this.feedback.set(null);
    this.orderSelection.set([]);
    this.speakInstruction();
  }

  protected restartExercise(): void {
    this.questions.set(generateExerciseSession(this.activeExercise().kind));
    this.resetSession();
    this.screen.set('exercise');
    this.speakInstruction();
  }

  private setExercises(exercises: readonly ExerciseDefinition[]): void {
    this.exercises = exercises;
    this.activityGroups = buildActivityMenuGroups(exercises);
  }

  private activeSpeechLocale(): SpeechLocale {
    return this.activeExercise().speechLocale ?? 'it-IT';
  }

  private splitOrderVisual(visual: string): string[] {
    return visual.split(/\s*➡️\s*/u).map((part) => part.trim()).filter(Boolean);
  }

  private splitOrderLabel(label: string): string[] {
    return label.replace(/^prima\s+/iu, '').split(/\s+poi\s+/iu).map((part) => part.trim()).filter(Boolean);
  }

  private reversedOrderAnswerValue(question: Extract<ExerciseQuestion, { kind: 'instruction' }>): number {
    const correctChoice = question.choices.find((choice) => choice.value === question.expectedAnswer);
    if (!correctChoice) {
      return question.choices.find((choice) => choice.value !== question.expectedAnswer)?.value ?? question.expectedAnswer;
    }
    const correctParts = this.splitOrderVisual(correctChoice.visual);
    if (correctParts.length === 2) {
      const reversed = question.choices.find((choice) => {
        const parts = this.splitOrderVisual(choice.visual);
        return parts.length === 2 && parts[0] === correctParts[1] && parts[1] === correctParts[0];
      });
      if (reversed) {
        return reversed.value;
      }
    }
    return question.choices.find((choice) => choice.value !== question.expectedAnswer)?.value ?? question.expectedAnswer;
  }

  private shouldReverseOrderTiles(questionId: string): boolean {
    let hash = 0;
    for (const character of questionId) {
      hash = (hash * 31 + character.codePointAt(0)!) >>> 0;
    }
    return hash % 2 === 0;
  }

  private resetSession(): void {
    this.questionIndex.set(0);
    this.feedback.set(null);
    this.orderSelection.set([]);
    this.sessionAttempts.set(0);
  }
}
